import { graphql } from 'react-relay';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import Button from '@atlaskit/button/button';
import type { ContentProps } from '@atlaskit/popup/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';

import { expValEquals } from '@confluence/feature-experiments';
import { CONTENT_OWNERSHIP_DIALOG_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import type {
	ContentOwnerPopupComponentContentOwnerQuery as ContentOwnerPopupComponentContentOwnerQuery$data,
	ContentOwnerPopupComponentContentOwnerQueryVariables as ContentOwnerPopupComponentContentOwnerQuery$variables,
} from './__types__/ContentOwnerPopupComponentContentOwnerQuery.ts';
import { useExperienceTrackerContext } from './content-ownership-helpers.ts';
import { ContentOwnershipContextConsumer } from './ContentOwnershipContext.tsx';
import { useIsAllowedToChangeOwner } from './useIsAllowedToChangeOwner.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const i18n = defineMessages({
	creator: {
		id: 'content-ownership.content-owner-popup.creator',
		defaultMessage: '<b>Creator</b>',
		description: 'Label for a dropdown menu that informs users who created the content',
	},
	owner: {
		id: 'content-ownership.content-owner-popup.owner',
		defaultMessage: '<b>Owner</b>',
		description: 'Label for a dropdown menu that informs users who owns the content',
	},
	changeOwner: {
		id: 'content-ownership.content-owner-popup.change-owner',
		defaultMessage: 'Change owner',
		description: 'Text for a dropdown menu item that lets users change the owner of content',
	},
	ariaLabel: {
		id: 'content-ownership.content-owner-popup.ariaLabel',
		defaultMessage: 'More information about {name}',
		description: 'Aria label attribute of the text displaying a creator or owner name',
	},
});

// fixed dimensions
const contentOwnerContainerStyles = xcss({
	width: '232px',
	height: '180px',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	padding: 'space.250',
});

const contentOwnerViewerContainerStyles = xcss({
	width: '232px',
	height: '140px',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	padding: 'space.250',
});

const contentOwnerItemStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const contentCreatorItemStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	paddingBottom: 'space.100',
});

const contentOwnerChangeItemStyles = xcss({
	marginTop: 'space.100',
});

const contentOwnerNameItemStyles = xcss({
	marginTop: 'space.025',
	textDecoration: 'underline',
	color: 'color.text',
	':hover': {
		color: 'color.link',
		cursor: 'pointer',
	},
});

type UserData = {
	userId: string;
	fullName: string;
	isAnonymous: boolean;
};

interface ContentOwnerDetailsPanelWrapperProps {
	ownedBy: UserData;
	createdBy: UserData;
	closePopup: () => void;
	setInitialFocusRef?: ContentProps['setInitialFocusRef'];
}

const ContentOwnerDetailsPanelName = ({ userId, fullName, ariaLabel }) => {
	return (
		<Box xcss={contentOwnerNameItemStyles} role="tooltip" aria-label={ariaLabel}>
			<ProfileCardWrapper
				userId={userId}
				position="left-start"
				offset={[0, 28]}
				trigger={
					expValEquals('confluence_profile_card_consistent_updates', 'isEnabled', true)
						? 'click'
						: 'hover'
				}
				fullName={fullName}
			>
				{fullName}
			</ProfileCardWrapper>
		</Box>
	);
};

const ContentOwnerDetailsPanel = ({
	ownedBy,
	createdBy,
	closePopup,
	openModal,
	setInitialFocusRef,
}) => {
	const intl = useIntl();
	const experienceTracker = useExperienceTrackerContext();
	const onClickChangeOwner = useCallback(() => {
		openModal();
		closePopup();
		experienceTracker.start({
			name: CONTENT_OWNERSHIP_DIALOG_EXPERIENCE,
		});
	}, [closePopup, openModal, experienceTracker]);
	const isAllowedToChange = useIsAllowedToChangeOwner(ownedBy.userId);

	return (
		<Box
			xcss={[isAllowedToChange ? contentOwnerContainerStyles : contentOwnerViewerContainerStyles]}
		>
			<Box xcss={contentCreatorItemStyles}>
				<FormattedMessage
					{...i18n.creator}
					values={{
						b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
					}}
				/>
				<ContentOwnerDetailsPanelName
					userId={createdBy.userId}
					fullName={createdBy.fullName}
					ariaLabel={intl.formatMessage(i18n.ariaLabel, {
						name: createdBy.fullName,
					})}
				/>
			</Box>
			<Box xcss={contentOwnerItemStyles}>
				<FormattedMessage
					{...i18n.owner}
					values={{
						b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
					}}
				/>
				<ContentOwnerDetailsPanelName
					userId={ownedBy.userId}
					fullName={ownedBy.fullName}
					ariaLabel={intl.formatMessage(i18n.ariaLabel, {
						name: ownedBy.fullName,
					})}
				/>
			</Box>
			{isAllowedToChange && (
				<Box xcss={contentOwnerChangeItemStyles}>
					<Button
						onClick={onClickChangeOwner}
						appearance="link"
						spacing="none"
						ref={setInitialFocusRef}
					>
						{intl.formatMessage(i18n.changeOwner)}
					</Button>
				</Box>
			)}
		</Box>
	);
};

const ContentOwnerDetailsPanelWrapper: React.FC<ContentOwnerDetailsPanelWrapperProps> = ({
	ownedBy,
	createdBy,
	closePopup,
	setInitialFocusRef,
}) => {
	if (!ownedBy || !createdBy) {
		return null;
	}

	return (
		<ContentOwnershipContextConsumer>
			{({ openModal }) => (
				<ContentOwnerDetailsPanel
					ownedBy={ownedBy}
					createdBy={createdBy}
					closePopup={closePopup}
					openModal={openModal}
					setInitialFocusRef={setInitialFocusRef}
				/>
			)}
		</ContentOwnershipContextConsumer>
	);
};

interface ContentOwnerPopupComponentProps {
	contentId: string;
	itemId: string;
	itemLabel?: string | null;
	isOpen: boolean;
	onClick: () => void;
	onCloseParent?: () => void;
	onClosePopup: () => void;
	onMouseEnter?: () => void;
	elemBefore?: React.ReactNode;
}

export const ContentOwnerPopupComponent: React.FC<ContentOwnerPopupComponentProps> = ({
	contentId,
	itemId,
	itemLabel,
	isOpen,
	onClick,
	onCloseParent,
	onClosePopup,
	onMouseEnter,
	elemBefore,
}) => {
	const closeOwnerDialog = () => {
		onClosePopup();
		onCloseParent?.();
	};
	const { data: ownerHistoryData } = useQueryInterop<
		ContentOwnerPopupComponentContentOwnerQuery$data,
		ContentOwnerPopupComponentContentOwnerQuery$variables
	>(
		graphql`
			query ContentOwnerPopupComponentContentOwnerQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					history {
						createdBy {
							displayName
							... on KnownUser {
								accountId
							}
						}
						ownedBy {
							displayName
							... on KnownUser {
								accountId
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			skip: !contentId,
		},
	);
	const toUserData = (user) => ({
		userId: user?.accountId ?? undefined,
		fullName: user?.displayName,
		isAnonymous: !user?.accountId,
	});
	const ownedBy = toUserData(ownerHistoryData?.singleContent?.history?.ownedBy);
	const createdBy = toUserData(ownerHistoryData?.singleContent?.history?.createdBy);

	return (
		<Popup
			key={`container-${itemId}`}
			isOpen={isOpen}
			onClose={onClosePopup}
			// @ts-ignore onBlur not a supported Popup prop, but works as intended. Keep this around until live pages behavior is finalized
			onBlur={onClosePopup}
			placement="left-start"
			content={({ setInitialFocusRef }) => (
				<ContentOwnerDetailsPanelWrapper
					ownedBy={ownedBy}
					createdBy={createdBy}
					closePopup={closeOwnerDialog}
					setInitialFocusRef={setInitialFocusRef}
				/>
			)}
			trigger={(triggerProps) => (
				<DropdownItem
					elemBefore={elemBefore}
					{...triggerProps}
					onClick={onClick}
					// @ts-ignore onMouseEnter not a supported Popup prop, but works as intended. Keep this around until live pages behavior is finalized
					onMouseEnter={onMouseEnter}
					isSelected={isOpen}
					elemAfter={<ChevronRightIcon label="" color={token('color.icon')} size="small" />}
				>
					<span>{itemLabel}</span>
				</DropdownItem>
			)}
		/>
	);
};
